import type { PageContainerProps as AntdPageContainerProps } from '@ant-design/pro-components'
import type { AlertProps } from 'antd'
import { Tabs, type TabsProps } from 'antd'
import {
  Children,
  createContext,
  createElement,
  type DependencyList,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

export type PageContainerBreadcrumbMode = 'auto' | 'off' | 'custom'

export type RuntimePageContainerProps = Partial<Omit<AntdPageContainerProps, 'children'>> & {
  renderLastAsLink?: boolean
  maxItems?: number
  breadcrumbMode?: PageContainerBreadcrumbMode
  alerts?: AlertProps[]
  alertAdditions?: AlertProps[]
  extraAdditions?: ReactNode[]
}

type RuntimeStoreContextValue = {
  upsert: (id: symbol, props: RuntimePageContainerProps | null | undefined) => void
  remove: (id: symbol) => void
  mergedProps: RuntimePageContainerProps
}

const RuntimeStoreContext = createContext<RuntimeStoreContextValue | null>(null)

export function PageContainerRuntimeProvider({ children }: PropsWithChildren) {
  const [entries, setEntries] = useState<Map<symbol, RuntimePageContainerProps>>(new Map())

  const upsert = useCallback((id: symbol, props: RuntimePageContainerProps | null | undefined) => {
    setEntries(current => {
      if (!props) {
        if (!current.has(id)) {
          return current
        }
        const next = new Map(current)
        next.delete(id)
        return next
      }

      if (current.get(id) === props) {
        return current
      }

      const next = new Map(current)
      next.set(id, props)
      return next
    })
  }, [])

  const remove = useCallback((id: symbol) => {
    setEntries(current => {
      if (!current.has(id)) {
        return current
      }
      const next = new Map(current)
      next.delete(id)
      return next
    })
  }, [])

  const mergedProps = useMemo(() => {
    let merged: RuntimePageContainerProps = {}
    const mergedAlertAdditions: AlertProps[] = []
    const mergedExtraAdditions: ReactNode[] = []

    for (const value of entries.values()) {
      const { alertAdditions, extraAdditions, ...rest } = value

      merged = {
        ...merged,
        ...rest,
        header: {
          ...merged.header,
          ...rest.header,
        },
      }

      if (alertAdditions?.length) {
        mergedAlertAdditions.push(...alertAdditions)
      }

      if (extraAdditions?.length) {
        mergedExtraAdditions.push(...extraAdditions)
      }
    }

    if (mergedAlertAdditions.length > 0) {
      merged.alerts = [...(merged.alerts ?? []), ...mergedAlertAdditions]
    }

    if (mergedExtraAdditions.length > 0) {
      const existingExtra = Children.toArray(merged.header?.extra)
      merged.header = {
        ...merged.header,
        extra: [...existingExtra, ...mergedExtraAdditions],
      }
    }

    return merged
  }, [entries])

  const contextValue = useMemo(
    () => ({
      upsert,
      remove,
      mergedProps,
    }),
    [mergedProps, remove, upsert],
  )

  return createElement(RuntimeStoreContext.Provider, { value: contextValue }, children)
}

export function usePageContainerOverride(
  overrides: RuntimePageContainerProps | null | undefined,
  deps: DependencyList = [],
) {
  const context = useContext(RuntimeStoreContext)
  const idRef = useRef(Symbol('page-container-override'))
  const overridesRef = useRef(overrides)
  const upsert = context?.upsert
  const remove = context?.remove

  overridesRef.current = overrides

  useEffect(() => {
    if (!upsert || !remove) {
      return
    }
    upsert(idRef.current, overridesRef.current)
    return () => {
      remove(idRef.current)
    }
  }, [remove, upsert, ...deps])
}

export function usePageContainerController() {
  const context = useContext(RuntimeStoreContext)
  const idRef = useRef(Symbol('page-container-controller'))

  useEffect(() => {
    if (!context) {
      return
    }
    return () => {
      context.remove(idRef.current)
    }
  }, [context])

  return {
    setPageContainerProps: (props: RuntimePageContainerProps | null | undefined) => {
      context?.upsert(idRef.current, props)
    },
    clearPageContainerProps: () => {
      context?.remove(idRef.current)
    },
  }
}

export function useTabs({ type = 'line', ...tabsProps }: TabsProps, deps: DependencyList = []) {
  usePageContainerOverride(
    {
      header: {
        footer: createElement(Tabs, {
          type,
          ...tabsProps,
        }),
      },
    },
    deps,
  )
}

export function useAlerts(alerts: AlertProps[] | null | undefined, deps: DependencyList = []) {
  usePageContainerOverride(
    {
      alertAdditions: alerts ?? undefined,
    },
    deps,
  )
}

export function useAlert(alert: AlertProps | null | undefined, deps: DependencyList = []) {
  useAlerts(alert ? [alert] : undefined, deps)
}

export function useExtras(extras: ReactNode[] | null | undefined, deps: DependencyList = []) {
  usePageContainerOverride(
    {
      extraAdditions: extras ?? undefined,
    },
    deps,
  )
}

export function useExtra(extra: ReactNode | null | undefined, deps: DependencyList = []) {
  useExtras(extra == null ? undefined : [extra], deps)
}

export function usePageContainerRuntimeProps(): RuntimePageContainerProps {
  const context = useContext(RuntimeStoreContext)
  return context?.mergedProps ?? {}
}
